/* eslint-disable dot-notation */
import React, { FC, useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Formik } from "formik";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
// controls
import { Form, Button as BtButton } from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Plus, BoxArrowDown } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { useGetExampleFileQuery } from "features/api/contentManagement/contentManagementApiSlice";
import {
  UpdateFormValues,
  UpdateFormValidations,
} from "../contentManagementUtils/tabSchema";
import { getFormattedDateAndTime } from "../contentManagementUtils/contentManagementUtils";

type AddContentFormModalProps = {
  isOpen: boolean;
  toggle: (initialValues: null | UpdateFormValues) => void;
  submitForm: (values: UpdateFormValues) => void;
  headerTitle: string;
  approveBtnLabel: string;
  cancelBtnLabel: string;
  initialValues: UpdateFormValues;
  validationSchema: Yup.ObjectSchema<UpdateFormValidations>;
};

const AddUpdatesContentForm: FC<AddContentFormModalProps> = ({
  isOpen,
  toggle,
  submitForm,
  headerTitle,
  approveBtnLabel,
  cancelBtnLabel,
  initialValues,
  validationSchema,
}) => {
  const { t } = useTranslation();
  const formikProps = {
    initialValues: {
      ...initialValues,
      fileName: initialValues.fileName ?? "",
      url: initialValues.url ?? "",
    },
    onSubmit: submitForm,
    validationSchema,
  };

  const fileInput = useRef(null);
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  const [isShowInApp, setIsShowInApp] = useState(initialValues["showInApp"]);
  const [selectedFile, setSelectedFile] = useState(() => {
    return initialValues["fileName"]
      ? {
          name: initialValues["fileName"],
          size: 0,
        }
      : null;
  });
  const [selectedExpirationDate, setSelectedExpirationDate] = useState(
    new Date(initialValues["expirationDate"])
  );
  const [triggerDownload, setTriggerDownload] = useState(false);

  const { data: exampleFile } = useGetExampleFileQuery(undefined, {
    skip: triggerDownload === false,
    refetchOnMountOrArgChange: triggerDownload,
  });

  useEffect(() => {
    if (exampleFile) {
      const downloadUrl = window.URL.createObjectURL(exampleFile);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "example_file.xlsx"); // Set the desired file name
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl); // Clean up
      setTriggerDownload(false); // Reset trigger
    }
  }, [exampleFile]);

  const handleDownloadExampleFile = () => {
    setTriggerDownload(true); // Trigger the query
  };

  const [errorUploadFile, setErrorUploadFile] = useState("");

  const setError = (errorText: string) => {
    setErrorUploadFile(errorText);
    fileInput.current.value = ""; // Reset the input field
  };

  // const handleDownloadExampleFile = async () => {
  //   try {
  //     const response = await fetch('YOUR_API_ENDPOINT');
  //     const blob = await response.blob();
  //     const downloadUrl = window.URL.createObjectURL(blob);
  //     const link = document.createElement('a');
  //     link.href = downloadUrl;
  //     link.setAttribute('download', 'example_file.xlsx'); // or the filename from the Content-Disposition header
  //     document.body.appendChild(link);
  //     link.click();
  //     link.parentNode.removeChild(link);
  //   } catch (error) {
  //     console.error('Error downloading the file:', error);
  //   }
  // };

  const handleFileChange = (event, setFieldValue) => {
    const file = event.target.files[0];
    const allowedExtensions = /(\.xlsx|\.xls)$/i; // Regular expression to match the desired extensions
    const maxSizeInBytes = 10 * 1024 * 1024;

    if (!allowedExtensions.exec(file.name)) {
      setError(t("invalidTypeFile"));
    } else if (file && file.size > maxSizeInBytes) {
      setError(t("invalidSizeFile"));
    } else {
      setErrorUploadFile("");
      setSelectedFile(file);
      setFieldValue("fileName", file.name);
      setFieldValue("file", file);
    }
  };

  const handleRadioChange = (event) => {
    const { value } = event.target;
    const isApp = value === "app";

    setIsShowInApp(isApp);
  };

  return (
    <Modal
      size="lg"
      show={isOpen}
      onHide={() => toggle(null)}
      aria-labelledby="contained-modal-title-vcenter"
      contentClassName="form-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{headerTitle}</Modal.Title>
      </Modal.Header>
      <Formik {...formikProps}>
        {({
          handleSubmit,
          handleChange,
          touched,
          values,
          errors,
          setFieldValue,
        }) => {
          return (
            <>
              <Modal.Body>
                <Form>
                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column xs={3}>
                      {t("creationDate")}
                    </Form.Label>
                    <Form.Label column xs={3}>
                      {getFormattedDateAndTime(values["createdOn"])}
                    </Form.Label>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column xs={3}>
                      {t("systemOrApp")}
                    </Form.Label>
                    <Col>
                      <div className="flex-row">
                        <Form.Check
                          type="radio"
                          id="systemRadio"
                          value="system"
                          name="showInApp"
                          style={{ marginLeft: "10px" }}
                          checked={!isShowInApp}
                          onChange={(e) => {
                            handleRadioChange(e);
                            setFieldValue("showInApp", false);
                            setFieldValue("isPushNotification", false);
                            setErrorUploadFile("");
                          }}
                        />
                        <Form.Check.Label
                          htmlFor="systemRadio"
                          style={{ marginLeft: "10px" }}
                        >
                          {t("systemShort")}
                        </Form.Check.Label>

                        <Form.Check
                          type="radio"
                          id="appRadio"
                          value="app"
                          name="showInApp"
                          style={{ marginLeft: "10px" }}
                          checked={isShowInApp}
                          onChange={(e) => {
                            handleRadioChange(e);
                            setFieldValue("showInApp", true);
                          }}
                        />
                        <Form.Check.Label htmlFor="appRadio">
                          {t("application")}
                        </Form.Check.Label>
                      </div>
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column xs={3} htmlFor="isActive">
                      {t("activeQuestion")}
                    </Form.Label>
                    <Col>
                      <Form.Check
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        style={{
                          marginLeft: "10px",
                        }}
                        checked={values["isActive"]}
                        onChange={handleChange}
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column xs={3}>
                      {t("titleLabel")}
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        id="headLine"
                        name="headLine"
                        value={values["headLine"]}
                        onChange={handleChange}
                        isInvalid={
                          touched?.["headLine"] && !!errors?.["headLine"]
                        }
                        maxLength={46}
                      />
                      {errors?.["headLine"] && (
                        <Form.Control.Feedback type="invalid">
                          {errors?.["headLine"]}
                        </Form.Control.Feedback>
                      )}
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column xs={3}>
                      {t("contentLabel")}
                    </Form.Label>
                    <Col>
                      <Form.Control
                        as="textarea"
                        rows={10}
                        placeholder={t("contentPlaceholder")}
                        id="content"
                        name="content"
                        value={values["content"]}
                        onChange={handleChange}
                        isInvalid={
                          touched?.["content"] && !!errors?.["content"]
                        }
                        maxLength={1000}
                      />
                      {errors?.["content"] && (
                        <Form.Control.Feedback type="invalid">
                          {errors?.["content"]}
                        </Form.Control.Feedback>
                      )}
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column xs={3}>
                      {t("linkLabel")}
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        id="url"
                        name="url"
                        placeholder={t("linkPlaceholder")}
                        value={values["url"]}
                        onChange={handleChange}
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column xs={3}>
                      {t("newsExpiration")}
                    </Form.Label>
                    <Col>
                      <DatePicker
                        className="form-control"
                        name="expirationDate"
                        selected={selectedExpirationDate}
                        onKeyDown={(e) => e.preventDefault()}
                        onSelect={(date) => {
                          setSelectedExpirationDate(date);
                          setFieldValue(
                            "expirationDate",
                            new Date(date).toISOString()
                          );
                        }}
                        dateFormat="dd/MM/yyyy"
                        minDate={currentDate}
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group
                    as={Row}
                    className="mb-3"
                    style={{
                      visibility: isShowInApp ? "visible" : "hidden",
                      alignItems: "center",
                    }}
                  >
                    <Form.Label column xs={3}>
                      {t("attachDocument")}
                      <br />
                      <Form.Label style={{ fontSize: 12 }}>
                        {t("maxFileSize10MB")}
                      </Form.Label>
                    </Form.Label>
                    <Col>
                      <div
                        className="flex-row"
                        style={{ justifyContent: "space-between" }}
                      >
                        {!selectedFile && (
                          <div>
                            <div className="flex-row">
                              <Form.Label
                                htmlFor="upload"
                                className="custom-file-upload"
                              >
                                <Plus color="#fff" />
                                <input
                                  ref={fileInput}
                                  id="upload"
                                  type="file"
                                  onChange={(e) =>
                                    handleFileChange(e, setFieldValue)
                                  }
                                  accept=".xlsx, .xls"
                                />
                              </Form.Label>
                              <Form.Label
                                htmlFor="upload"
                                className="upload-label"
                              >
                                {t("uploadFile")}
                              </Form.Label>
                            </div>
                            {errorUploadFile && (
                              <div className="requiredField">
                                {errorUploadFile}
                              </div>
                            )}
                          </div>
                        )}
                        {selectedFile && (
                          <div
                            className="flex-row"
                            style={{
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <div>{selectedFile.name}</div>
                            {selectedFile.size / (1024 * 1024) > 1 && (
                              <div>
                                ({Math.round(selectedFile.size / (1024 * 1024))}
                                mb)
                              </div>
                            )}
                            <button
                              type="button"
                              className="text-button"
                              onClick={() => {
                                setFieldValue("fileName", "");
                                setSelectedFile(null);
                              }}
                            >
                              {t("cancel")}
                            </button>
                          </div>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column xs={3}>
                      {t("exampleTemplate")}
                    </Form.Label>
                    <Col>
                      <div className="flex-row">
                        <Form.Label
                          className="custom-file-upload"
                          onClick={handleDownloadExampleFile}
                        >
                          <BoxArrowDown />
                        </Form.Label>

                        <Form.Label
                          className="upload-label"
                          onClick={handleDownloadExampleFile}
                        >
                          {t("downloadExampleFile")}
                        </Form.Label>
                      </div>
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column xs={3} htmlFor="isPositiveUpdate">
                      {t("positiveReinforcementQuestion")}
                    </Form.Label>
                    <Col>
                      <Form.Check
                        type="checkbox"
                        id="isPositiveUpdate"
                        name="isPositiveUpdate"
                        style={{
                          marginLeft: "10px",
                        }}
                        checked={values["isPositiveUpdate"]}
                        onChange={handleChange}
                      />
                    </Col>
                  </Form.Group>
                </Form>
              </Modal.Body>

              <Modal.Footer className="updates-modal-footer">
                <div
                  className="flex-row"
                  style={{ visibility: isShowInApp ? "visible" : "hidden" }}
                >
                  <Form.Check
                    type="checkbox"
                    id="isPushNotification"
                    name="isPushNotification"
                    style={{
                      marginLeft: "5px",
                    }}
                    checked={values["isPushNotification"]}
                    onChange={handleChange}
                  />
                  <Form.Check.Label htmlFor="isPushNotification">
                    {t("showNewsInPush")}
                  </Form.Check.Label>
                  <Form.Check
                    type="checkbox"
                    id="isPositiveUpdate"
                    name="isPositiveUpdate"
                    style={{
                      marginLeft: "5px",
                      marginRight: "15px",
                    }}
                    checked={values["isPositiveUpdate"]}
                    onChange={handleChange}
                  />
                  <Form.Check.Label htmlFor="isPositiveUpdate">
                    {t("positiveReinforcement")}
                  </Form.Check.Label>
                </div>
                <div className="updates-modal-buttons-container flex-row">
                  <BtButton
                    variant="secondary"
                    className="updates-modal-button"
                    style={{ marginLeft: 10 }}
                    onClick={() => {
                      setSelectedFile(null);
                      toggle(null);
                    }}
                  >
                    {cancelBtnLabel}
                  </BtButton>
                  <BtButton
                    variant="primary"
                    className="updates-modal-button"
                    onClick={() => handleSubmit()}
                    disabled={isShowInApp && errorUploadFile !== ""}
                  >
                    {approveBtnLabel}
                  </BtButton>
                </div>
              </Modal.Footer>
            </>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AddUpdatesContentForm;
